<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const selectedCampaign = ref(null);
const scheduledAt = ref('');

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const toLocalDateTime = value => {
  if (!value) return '';

  const date = new Date(typeof value === 'number' ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) return '';

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const currentDateTime = computed(() =>
  toLocalDateTime(Math.floor(Date.now() / 1000))
);
const selectedCampaignTitle = computed(
  () => selectedCampaign.value?.title || ''
);
const isInvalid = computed(() => {
  if (!selectedCampaign.value || !scheduledAt.value) return true;

  const timestamp = new Date(scheduledAt.value).getTime();
  return Number.isNaN(timestamp) || timestamp <= Date.now();
});

const open = campaign => {
  selectedCampaign.value = campaign;
  scheduledAt.value = toLocalDateTime(campaign?.scheduled_at);
  dialogRef.value?.open();
};

const handleSubmit = async () => {
  if (isInvalid.value) return;

  try {
    await store.dispatch('campaigns/update', {
      id: selectedCampaign.value.id,
      scheduled_at: new Date(scheduledAt.value).toISOString(),
    });
    useAlert(t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.API.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.API.ERROR_MESSAGE'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.TITLE')"
    :description="
      t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.DESCRIPTION', {
        campaign: selectedCampaignTitle,
      })
    "
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.CONFIRM')"
    :is-loading="isUpdating"
    :disable-confirm-button="isInvalid || isUpdating"
    @confirm="handleSubmit"
  >
    <Input
      v-model="scheduledAt"
      type="datetime-local"
      :min="currentDateTime"
      :label="t('CAMPAIGN.WHATSAPP.EDIT_SCHEDULE.SCHEDULED_AT')"
    />
  </Dialog>
</template>
